import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { format, differenceInHours } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Clock, AlertTriangle, Eye, XCircle } from "lucide-react";
import OpenSessionModal from "./OpenSessionModal";
import CloseSessionModal from "./CloseSessionModal";
import SessionDetailsModal from "./SessionDetailsModal";
import OpenSessionDetailsModal from "./OpenSessionDetailsModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { MoneyInput } from "@/components/ui/money-input";
import { MoneyValue } from "@/components/MoneyValue";

export default function CashRegisterSessions() {
  const { tenantId } = useTenant();
  const { isAdminTenant, isAdminSaas } = usePermissions();
  const canManageSessions = isAdminTenant || isAdminSaas;
  const queryClient = useQueryClient();
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showOpenDetailsModal, setShowOpenDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editOpeningNotes, setEditOpeningNotes] = useState("");
  const [editClosingNotes, setEditClosingNotes] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openingMap, setOpeningMap] = useState<Record<string, number>>({});
  const [closingMap, setClosingMap] = useState<Record<string, number>>({});
  const [closingExpectedMap, setClosingExpectedMap] = useState<Record<string, number>>({});
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  const [openingNotesMap, setOpeningNotesMap] = useState<Record<string, string>>({});
  const [closingNotesMap, setClosingNotesMap] = useState<Record<string, string>>({});

  //

  // Buscar sessões abertas
  const { data: openSessions, isLoading: loadingOpen } = useQuery({
    queryKey: ["open-sessions", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select(`
          *,
          bank_accounts (id, nome, icone),
          profiles!cash_register_sessions_opened_by_fkey (nome_completo)
        `)
        .eq("tenant_id", tenantId)
        .eq("status", "open")
        .order("opened_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Buscar sessões fechadas (últimos 30 dias)
  const { data: closedSessions, isLoading: loadingClosed } = useQuery({
    queryKey: ["closed-sessions", tenantId],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from("cash_register_sessions")
        .select(`
          *,
          bank_accounts (id, nome, icone),
          profiles!cash_register_sessions_opened_by_fkey (nome_completo),
          profiles_closed:profiles!cash_register_sessions_closed_by_fkey (nome_completo)
        `)
        .eq("tenant_id", tenantId)
        .eq("status", "closed")
        .gte("closed_at", thirtyDaysAgo.toISOString())
        .order("closed_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  const handleCloseSession = (session: any) => {
    setSelectedSession(session);
    setShowCloseModal(true);
  };

  const handleViewDetails = (session: any) => {
    setSelectedSession(session);
    if (session?.status === 'open') {
      setShowOpenDetailsModal(true);
    } else {
      setShowDetailsModal(true);
    }
  };

  const handleEditSession = (session: any) => {
    setSelectedSession(session);
    setEditOpeningNotes(session.opening_notes || "");
    setEditClosingNotes(session.closing_notes || "");
    setShowEditModal(true);
  };

  // Carregar breakdowns quando abrir o modal de edição
  useQuery({
    queryKey: ["edit-session-breakdowns", selectedSession?.id, showEditModal],
    queryFn: async () => {
      if (!selectedSession?.id || !showEditModal) return null;
      const { data, error } = await supabase
        .from("cash_register_breakdown")
        .select("bank_account_id, expected_amount, declared_amount, notes, phase, bank_accounts(nome)")
        .eq("session_id", selectedSession.id)
        .is("payment_method_id", null);
      if (error) throw error;
      const openMap: Record<string, number> = {};
      const closeMap: Record<string, number> = {};
      const closeExp: Record<string, number> = {};
      const names: Record<string, string> = {};
      const openNotes: Record<string, string> = {};
      const closeNotes: Record<string, string> = {};
      (data || []).forEach((r: any) => {
        names[r.bank_account_id] = r.bank_accounts?.nome || r.bank_account_id;
        if (r.phase === 'opening') {
          openMap[r.bank_account_id] = Number(r.declared_amount ?? r.expected_amount ?? 0) || 0;
          openNotes[r.bank_account_id] = r.notes || "";
        } else if (r.phase === 'closing') {
          closeMap[r.bank_account_id] = Number(r.declared_amount ?? 0) || 0;
          closeExp[r.bank_account_id] = Number(r.expected_amount ?? 0) || 0;
          closeNotes[r.bank_account_id] = r.notes || "";
        }
      });
      setOpeningMap(openMap);
      setClosingMap(closeMap);
      setClosingExpectedMap(closeExp);
      setAccountNames(names);
      setOpeningNotesMap(openNotes);
      setClosingNotesMap(closeNotes);
      return data;
    },
    enabled: !!selectedSession?.id && showEditModal,
  });

  // Salvamento único: notas gerais + valores por conta

  const saveAmountsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSession?.id) return;
      const sid = selectedSession.id as string;
      // Atualiza notas gerais da sessão
      {
        const { error } = await supabase
          .from("cash_register_sessions")
          .update({ opening_notes: editOpeningNotes, closing_notes: editClosingNotes } as any)
          .eq("id", sid);
        if (error) throw error;
      }
      // Para garantir persistência, atualiza se existir; caso contrário, cria o registro
      // Abertura (phase='opening')
      for (const [acc, val] of Object.entries(openingMap)) {
        const { data: openRow, error: openSelErr } = await supabase
          .from("cash_register_breakdown")
          .select("id")
          .eq("session_id", sid)
          .eq("phase", "opening")
          .is("payment_method_id", null)
          .eq("bank_account_id", acc)
          .maybeSingle();
        if (openSelErr && openSelErr.code !== "PGRST116") throw openSelErr;
        if (openRow?.id) {
          const { error } = await supabase
            .from("cash_register_breakdown")
            .update({ expected_amount: val, declared_amount: val, notes: openingNotesMap[acc] || null } as any)
            .eq("id", openRow.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("cash_register_breakdown")
            .insert({
              session_id: sid,
              bank_account_id: acc,
              payment_method_id: null,
              phase: "opening",
              expected_amount: val,
              declared_amount: val,
              notes: openingNotesMap[acc] || null,
            } as any);
          if (error) throw error;
        }
      }

      // Fechamento (phase='closing') - declared e difference
      for (const [acc, val] of Object.entries(closingMap)) {
        const exp = closingExpectedMap[acc] ?? 0;
        const diff = (val || 0) - (exp || 0);
        const { data: closeRow, error: closeSelErr } = await supabase
          .from("cash_register_breakdown")
          .select("id")
          .eq("session_id", sid)
          .eq("phase", "closing")
          .is("payment_method_id", null)
          .eq("bank_account_id", acc)
          .maybeSingle();
        if (closeSelErr && closeSelErr.code !== "PGRST116") throw closeSelErr;
        if (closeRow?.id) {
          const { error } = await supabase
            .from("cash_register_breakdown")
            .update({ declared_amount: val, /*difference: diff,*/ notes: closingNotesMap[acc] || null } as any)
            .eq("id", closeRow.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("cash_register_breakdown")
            .insert({
              session_id: sid,
              bank_account_id: acc,
              payment_method_id: null,
              phase: "closing",
              expected_amount: exp,
              declared_amount: val,
              // difference: diff, // coluna controlada pelo banco
              notes: closingNotesMap[acc] || null,
            } as any);
          if (error) throw error;
        }
      }
      // Atualiza totais da sessão (opening_balance e declared_balance)
      const openingTotal = Object.values(openingMap).reduce((s, v) => s + (v || 0), 0);
      const declaredTotal = Object.values(closingMap).reduce((s, v) => s + (v || 0), 0);
      const { error: sessErr } = await supabase
        .from("cash_register_sessions")
        .update({
          opening_balance: openingTotal,
          declared_balance: declaredTotal,
        } as any)
        .eq("id", sid);
      if (sessErr) throw sessErr;

      // Invalida caches e fecha modal
      queryClient.invalidateQueries({ queryKey: ["closed-sessions", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["open-sessions", tenantId] });
      setShowEditModal(false);
    },
    onError: (e: any) => toast.error(e?.message || "Erro ao salvar valores"),
  });

  const deleteSessionMutation = useMutation({
    mutationFn: async (id: string) => {
      // remover breakdowns primeiro (caso não haja cascade)
      const { error: bdErr } = await supabase
        .from("cash_register_breakdown")
        .delete()
        .eq("session_id", id);
      if (bdErr && bdErr.code && bdErr.code !== "PGRST116") {
        // ignora not found; qualquer outro erro lança
        throw bdErr;
      }
      const { error } = await supabase
        .from("cash_register_sessions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Sessão excluída");
      queryClient.invalidateQueries({ queryKey: ["closed-sessions", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["open-sessions", tenantId] });
      setDeletingId(null);
    },
    onError: (e: any) => toast.error(e?.message || "Erro ao excluir sessão"),
  });

  return (
    <div className="space-y-6">
      {/* Sessões Abertas: renderizar apenas se existir sessão */}
      <div>
        {(!loadingOpen && openSessions && openSessions.length > 0) && (
          <div className="grid gap-4 grid-cols-1">
            {openSessions.map((session) => {
              const hoursOpen = differenceInHours(new Date(), new Date(session.opened_at));
              const isLongSession = hoursOpen > 12;

              return (
                <Card key={session.id} className={isLongSession ? "border-destructive" : ""}>
                  <CardHeader className="pt-0 pr-0 pb-0">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {session.bank_accounts?.icone && (
                          <span>{session.bank_accounts.icone}</span>
                        )}
                        {session.bank_accounts?.nome}
                      </CardTitle>
                      {isLongSession && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          +12h
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="w-full grid grid-cols-1 md:grid-cols-4 items-center gap-3">
                      <div className="text-sm">
                        <p className="text-muted-foreground">Operador:</p>
                        <p className="font-medium">{session.profiles?.nome_completo}</p>
                      </div>
                      <div className="text-sm">
                        <p className="text-muted-foreground">Abertura:</p>
                        <p className="font-medium">
                          {format(new Date(session.opened_at), "dd/MM/yyyy 'às' HH:mm", {
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                      <div className="text-sm">
                        <p className="text-muted-foreground">Saldo da Abertura:</p>
                        <MoneyValue
                          asBlock
                          value={session.opening_balance || 0}
                          valueClassName="font-medium text-lg"
                          className="justify-start"
                        />
                      </div>
                      <div className="flex md:justify-end -mr-2 gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewDetails(session)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canManageSessions && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handleEditSession(session)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="icon-chip-red" onClick={() => setDeletingId(session.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => handleCloseSession(session)}
                          className="w-full md:w-auto"
                          variant="outline"
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Fechar Caixa
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        {(!loadingOpen && (!openSessions || openSessions.length === 0)) && (
          <div className="flex justify-end">
            <Button onClick={() => setShowOpenModal(true)}>
              <Clock className="mr-2 h-4 w-4" />
              Abrir Caixa
            </Button>
          </div>
        )}
      </div>

      {/* Sessões Fechadas */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Últimas aberturas e fechamentos de caixa</h2>

        {loadingClosed ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center">Carregando...</p>
            </CardContent>
          </Card>
        ) : !closedSessions || closedSessions.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center">
                Nenhuma sessão fechada nos últimos 30 dias
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data Fechamento</TableHead>
                    <TableHead>Conta</TableHead>
                    <TableHead>Operador</TableHead>
                    <TableHead className="text-right">Saldo Inicial</TableHead>
                    <TableHead className="text-right">Saldo Esperado</TableHead>
                    <TableHead className="text-right">Saldo Declarado</TableHead>
                    <TableHead className="text-right">Diferença</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {closedSessions.map((session) => {
                    const hasDifference = Math.abs(session.difference || 0) > 0.01;

                    return (
                      <TableRow key={session.id}>
                        <TableCell>
                          {format(new Date(session.closed_at!), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {session.bank_accounts?.icone && (
                              <span>{session.bank_accounts.icone}</span>
                            )}
                            {session.bank_accounts?.nome}
                          </div>
                        </TableCell>
                        <TableCell>{session.profiles?.nome_completo}</TableCell>
                        <TableCell className="text-right">
                          <MoneyValue asBlock value={session.opening_balance || 0} className="justify-end" />
                        </TableCell>
                        <TableCell className="text-right">
                          <MoneyValue asBlock value={session.expected_balance || 0} className="justify-end" />
                        </TableCell>
                        <TableCell className="text-right">
                          <MoneyValue asBlock value={session.declared_balance || 0} className="justify-end" />
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            hasDifference ? "text-destructive" : "text-muted-foreground"
                          }`}
                        >
                          <MoneyValue
                            asBlock
                            value={session.difference || 0}
                            valueClassName={`font-medium ${hasDifference ? "text-destructive" : "text-muted-foreground"}`}
                            className="justify-end"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleViewDetails(session)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canManageSessions && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleEditSession(session)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="icon-chip-red" onClick={() => setDeletingId(session.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modals */}
      <OpenSessionModal open={showOpenModal} onOpenChange={setShowOpenModal} />
      
      {selectedSession && (
        <>
          <CloseSessionModal
            open={showCloseModal}
            onOpenChange={setShowCloseModal}
            session={selectedSession}
          />
          {/* Detalhes conforme status */}
          <OpenSessionDetailsModal
            open={showOpenDetailsModal}
            onOpenChange={setShowOpenDetailsModal}
            session={selectedSession}
          />
          <SessionDetailsModal
            open={showDetailsModal}
            onOpenChange={setShowDetailsModal}
            sessionId={selectedSession.id}
          />
          {/* Editar notas da sessão */}
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="sm:max-w-[900px] h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Editar sessão</DialogTitle>
              </DialogHeader>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-6">

                {/* Abertura (linha completa) */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Abertura (esperado/declarado) por Conta</p>
                  <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr] text-xs text-muted-foreground px-1">
                    <div className="text-right pr-2">Nome da Conta</div>
                    <div className="text-center">Valor Declarado</div>
                    <div className="pl-2">Observação</div>
                  </div>
                  <div className="space-y-2">
                    {Object.keys(accountNames)
                      .sort((a, b) => (accountNames[a] || "").localeCompare(accountNames[b] || "", "pt-BR", { sensitivity: "base" }))
                      .map((id) => (
                      <div key={`open-${id}`} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
                        <span className="text-sm text-right md:pr-2">{accountNames[id]}</span>
                        <MoneyInput value={openingMap[id] || 0} onValueChange={(v) => setOpeningMap(prev => ({ ...prev, [id]: v }))} />
                        <input
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                          placeholder="Observação da abertura..."
                          value={openingNotesMap[id] || ""}
                          onChange={(e) => setOpeningNotesMap(prev => ({ ...prev, [id]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fechamento (linha completa) */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Fechamento (declarado) por Conta</p>
                  <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr] text-xs text-muted-foreground px-1">
                    <div className="text-right pr-2">Nome da Conta</div>
                    <div className="text-center">Valor Declarado</div>
                    <div className="pl-2">Observação</div>
                  </div>
                  <div className="space-y-2">
                    {Object.keys(accountNames)
                      .sort((a, b) => (accountNames[a] || "").localeCompare(accountNames[b] || "", "pt-BR", { sensitivity: "base" }))
                      .map((id) => (
                      <div key={`close-${id}`} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
                        <span className="text-sm text-right md:pr-2">{accountNames[id]}</span>
                        <MoneyInput value={closingMap[id] || 0} onValueChange={(v) => setClosingMap(prev => ({ ...prev, [id]: v }))} />
                        <input
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                          placeholder="Observação do fechamento..."
                          value={closingNotesMap[id] || ""}
                          onChange={(e) => setClosingNotesMap(prev => ({ ...prev, [id]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Observações (final) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Observações de Abertura</p>
                    <Textarea value={editOpeningNotes} onChange={(e) => setEditOpeningNotes(e.target.value)} rows={5} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Observações de Fechamento</p>
                    <Textarea value={editClosingNotes} onChange={(e) => setEditClosingNotes(e.target.value)} rows={5} />
                  </div>
                </div>
              </div>
              <div className="mt-auto -mx-6 -mb-6 px-6 py-3 border-t bg-background flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
                <Button onClick={() => saveAmountsMutation.mutate()} disabled={saveAmountsMutation.isPending}>
                  {saveAmountsMutation.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Excluir sessão */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir sessão?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá a sessão e seus lançamentos de breakdown. Não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingId && deleteSessionMutation.mutate(deletingId)} disabled={deleteSessionMutation.isPending}>
              {deleteSessionMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
